import React, { useEffect, useRef, useState } from "react";
import { Mic, MicOff, Loader2 } from "lucide-react";
import { API } from "../lib/api";

/**
 * Mic button that records short audio via MediaRecorder, uploads to
 * /api/voice/transcribe, and calls onTranscribed(text) when it returns.
 *
 * UX:
 *  - First click  → request mic permission, start recording (pulsing red)
 *  - Second click → stop, upload, transcribe → fires onTranscribed
 *  - Errors are shown inline with a tiny warning style
 *
 * Stops automatically after 25 seconds to keep transcription cheap and
 * avoid the 25MB whisper limit.
 */
const MAX_RECORD_MS = 25_000;

export default function VoiceButton({ onTranscribed, disabled, size = "sm", className = "" }) {
  const [state, setState] = useState("idle"); // idle | recording | uploading | error
  const [errMsg, setErrMsg] = useState("");
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const stopTimer = useRef(null);

  useEffect(() => () => cleanup(), []);

  const cleanup = () => {
    if (stopTimer.current) clearTimeout(stopTimer.current);
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      try { recorderRef.current.stop(); } catch { /* ignore */ }
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const start = async () => {
    setErrMsg("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = pickSupportedMime();
      const recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = handleStop;
      recorder.start();
      recorderRef.current = recorder;
      setState("recording");
      stopTimer.current = setTimeout(() => stop(), MAX_RECORD_MS);
    } catch (e) {
      console.error(e);
      setErrMsg("אין הרשאת מיקרופון");
      setState("error");
      setTimeout(() => setState("idle"), 2500);
    }
  };

  const stop = () => {
    if (recorderRef.current && recorderRef.current.state === "recording") {
      recorderRef.current.stop();
    }
  };

  const handleStop = async () => {
    cleanup();
    setState("uploading");
    try {
      const blob = new Blob(chunksRef.current, { type: chunksRef.current[0]?.type || "audio/webm" });
      const ext = (blob.type.split("/")[1] || "webm").split(";")[0];
      const fd = new FormData();
      fd.append("file", blob, `voice.${ext}`);
      const res = await fetch(`${API}/voice/transcribe`, { method: "POST", body: fd });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data.text) {
        onTranscribed?.(data.text);
        setState("idle");
      } else {
        setErrMsg("לא זוהה דיבור");
        setState("error");
        setTimeout(() => setState("idle"), 2500);
      }
    } catch (e) {
      console.error(e);
      setErrMsg("שגיאת תמלול");
      setState("error");
      setTimeout(() => setState("idle"), 2500);
    }
  };

  const handleClick = () => {
    if (disabled) return;
    if (state === "idle") start();
    else if (state === "recording") stop();
  };

  const sizeMap = {
    sm: "w-3.5 h-3.5",
    md: "w-4 h-4",
  }[size] || "w-3.5 h-3.5";

  const padMap = {
    sm: "p-1.5",
    md: "p-2",
  }[size] || "p-1.5";

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled || state === "uploading"}
      title={
        state === "recording" ? "לחץ לעצירה ושליחה" :
        state === "uploading" ? "מתמלל..." :
        state === "error" ? errMsg :
        "הקלט קולית"
      }
      className={`relative rounded-md transition-colors ${padMap} ${
        state === "recording"
          ? "text-severity-critical bg-severity-critical/15"
          : state === "uploading"
          ? "text-primary"
          : state === "error"
          ? "text-severity-watch"
          : "text-zinc-500 hover:text-primary hover:bg-primary/5"
      } ${className}`}
      data-testid="voice-button"
      data-state={state}
    >
      {state === "uploading" ? (
        <Loader2 className={`${sizeMap} animate-spin`} />
      ) : state === "error" ? (
        <MicOff className={sizeMap} />
      ) : (
        <Mic className={sizeMap} />
      )}
      {state === "recording" && (
        <>
          <span className="absolute inset-0 rounded-md bg-severity-critical/20 animate-ping" />
          <span className="absolute -top-1 -left-1 w-1.5 h-1.5 rounded-full bg-severity-critical" />
        </>
      )}
    </button>
  );
}

function pickSupportedMime() {
  if (typeof MediaRecorder === "undefined") return null;
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4",
    "audio/ogg;codecs=opus",
  ];
  for (const m of candidates) {
    if (MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(m)) return m;
  }
  return null;
}
